// Welke rijen horen bij een thema, en welke zijn effectbreed.
//
// Een thema is één doelgroep op één effect. Een segmentcel van díe doelgroep is
// 'eigen'; een ongesegmenteerde cel van het effect is 'effectbreed' en mag op
// het themascherm staan, mits gelabeld — een effectbrede uitspraak mag nooit
// als uitspraak over één doelgroep verschijnen (onderwerpmodel, besluit 2). Een
// cel van een ándere doelgroep zegt niets over dit thema en valt weg.
//
// AI-CONTEXT: See docs/features/intelligence-thema-eerst.md

/** @param {*} lijst @returns {Array<object>} */
const veilig = (lijst) => (Array.isArray(lijst) ? lijst.filter(Boolean) : [])

/** @param {*} theme @returns {boolean} */
const bruikbaar = (theme) => !!theme && typeof theme.msiId === 'string' && !!theme.msiId

/**
 * @param {{msiId: string, targetgroup: string}|null} theme
 * @param {Array<object>|null} rows - agendarijen uit buildAgendaRows
 * @returns {{own: Array<object>, effectWide: Array<object>}}
 */
export const splitAgendaRowsForTheme = (theme, rows) => {
    if (!bruikbaar(theme)) return { own: [], effectWide: [] }
    const vanEffect = veilig(rows).filter((row) => row.msiId === theme.msiId)
    return {
        own: vanEffect.filter((row) => row.segment === theme.targetgroup),
        effectWide: vanEffect.filter((row) => !row.segment),
    }
}

/**
 * TOC-keuzes kunnen (nog) niet per doelgroep geteld worden; ze zijn per
 * definitie effectbreed.
 *
 * @param {{msiId: string}|null} theme
 * @param {Array<object>|null} rows - keuzerijen uit usePathChoiceRows
 * @returns {Array<object>}
 */
export const choiceRowsForEffect = (theme, rows) => (
    bruikbaar(theme) ? veilig(rows).filter((row) => row.msiId === theme.msiId) : []
)

/**
 * Het effect van een inzicht zonder thema. `Params` is de levende parameterset;
 * de bevroren `Published.params` is wat er bij publiceren stond. Bij een
 * gepubliceerd inzicht zijn ze gelijk, maar de bevroren komt als tweede omdat
 * hij pas bestaat ná publiceren.
 *
 * @param {object} insight @returns {string}
 */
const effectVan = (insight) => (
    (insight.Params && insight.Params.msiId)
    || (insight.Published && insight.Published.params && insight.Published.params.msiId)
    || ''
)

/**
 * @param {{id: string, msiId: string}|null} theme
 * @param {Array<object>|null} insights - QuestionInsights-documenten
 * @returns {{own: Array<object>, effectWide: Array<object>}}
 */
export const splitPublishedForTheme = (theme, insights) => {
    if (!bruikbaar(theme)) return { own: [], effectWide: [] }
    const lijst = veilig(insights)
    return {
        own: lijst.filter((i) => i.ThemeID === theme.id),
        effectWide: lijst.filter((i) => !i.ThemeID && effectVan(i) === theme.msiId),
    }
}

/** Welke celdimensies onder welk tabblad vallen. Doelgroep- en contextcellen
 * horen bij geen tabblad (besluit user 2026-09-14) en vallen weg. */
const FAMILIES = Object.freeze({
    mechanisms: ['mechanismCategory', 'mechanismSignal'],
    activities: ['activity'],
})

/**
 * De eigen segmentcellen verdeeld over de tabbladen Mechanismen en Activiteiten.
 *
 * @param {Array<object>|null} own - eigen rijen uit splitAgendaRowsForTheme
 * @returns {{mechanisms: Array<object>, activities: Array<object>}}
 */
export const splitOwnRowsByFamily = (own) => {
    const lijst = veilig(own)
    return {
        mechanisms: lijst.filter((row) => FAMILIES.mechanisms.includes(row.dimension)),
        activities: lijst.filter((row) => FAMILIES.activities.includes(row.dimension)),
    }
}
